import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { useLatest, useUpdateEffect } from 'react-use';
import { Card, Stack, styled, Typography } from '@mui/material';

import { DrugsApi } from '@/api/domains/drugs.api';
import { DrugsQueryKeys } from '@/api/domains/drugs.query-keys';
import type { DrugInstructionForPharmacist } from '@/api/domains/drugs.types';
import { useGlobalErrorStore } from '@/components/dialogs';
import { CustomFormLabel, FormProvider, RHFTextField } from '@/components/form-controls';
import { RHFTextEditor } from '@/components/form-controls/RHFTextEditor';
import { ModalBodyContent } from '@/components/Modal';
import { ModalBody } from '@/components/Modal/ModalBody2';
import {
  NomenclatureConfirmationEditDialog,
  nomenclatureCreateEvent,
  useNomenclatureContextWindows,
  useNomenclatureStoreActions,
  useNomenclatureStorePreferredTab,
} from '@/components/nomenclature';
import { NomenclatureModalSidebar } from '@/components/nomenclature/modals/NomenclatureModalSidebar';
import type { NomenclatureModalTab } from '@/components/nomenclature/modals/NomenclatureModalTabbar';
import type { RecordStatusId } from '@/shared/constants/record-status.constants.ts';

import type { InstructionForPharmacistTabProps } from './InstructionForPharmacistTab';
import { useCreateInstructionForPharmacist } from './useCreateInstructionForPharmacist';

const TextEditorContainer = styled(Card)({
  height: '100%',
  minHeight: '100%',
  padding: 0,
});

const MultilineTextField = styled(RHFTextField)(() => ({
  marginTop: 2,
  '& ::-webkit-scrollbar': {
    width: 12,
  },
  '& ::-webkit-scrollbar-thumb': {
    backgroundColor: '#9AA7B5',
    border: '4px solid transparent',
    backgroundClip: 'padding-box',
    borderRadius: 9,
  },
}));

// ----------------------------------------------------------------------

type FormFields = DrugInstructionForPharmacist & {
  record_status_id: RecordStatusId;
};

type InstructionForPharmacistFormProps = InstructionForPharmacistTabProps & {
  instructionForPharmacist?: DrugInstructionForPharmacist & {
    record_status_id: RecordStatusId;
  };
};

const CURRENT_TAB: NomenclatureModalTab = 'instructionForPharmacist';

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Stack spacing={2} sx={{ mb: 5 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: '0.15px' }}>
      {title}
    </Typography>
    {children}
  </Stack>
);

const LabeledTextarea = ({ name, label }: { name: keyof FormFields; label: string }) => (
  <Stack spacing={0.5} sx={{ width: '100%' }}>
    <CustomFormLabel htmlFor={name}>{label}</CustomFormLabel>
    <MultilineTextField fullWidth name={name} variant="filled" multiline minRows={2} maxRows={4} />
  </Stack>
);

export const InstructionForPharmacistForm = (props: InstructionForPharmacistFormProps) => {
  const { instructionForPharmacist, drug, variant, onClose } = props;

  const queryClient = useQueryClient();
  const preferredTab = useNomenclatureStorePreferredTab();
  const {
    setActiveTab,
    setSelectedRow,
    openConfirmationEditDialog,
    closeConfirmationEditDialog,
    closeConfirmationCreateDialog,
  } = useNomenclatureStoreActions();
  const { mutateAsync: createInstructionForPharmacist } = useCreateInstructionForPharmacist();
  const { setGlobalError } = useGlobalErrorStore();
  const { isConfirmationCreateDialogVisible, isConfirmationEditDialogVisible } = useNomenclatureContextWindows();

  const isEditMode = variant === 'edit';
  const isEditAfterCreationMode = variant === 'editAfterCreation';

  const methods = useForm<FormFields>({
    defaultValues: instructionForPharmacist || {
      replacement_script: '',
      sale_script: '',
      recommendations: '',
      record_status_id: drug.record_status_id,
    },
  });

  const {
    formState: { isDirty, isSubmitting },
    handleSubmit,
  } = methods;

  const latestFormState = useLatest({ isDirty });

  const submitHandler: SubmitHandler<FormFields> = async data => {
    if (!isDirty) {
      onClose();
      return;
    }

    try {
      await createInstructionForPharmacist({ drugId: drug.id, ...data });

      isConfirmationCreateDialogVisible && closeConfirmationCreateDialog();
      isConfirmationEditDialogVisible && closeConfirmationEditDialog();

      const updatedDrug = await DrugsApi.updateDrug(drug.id, { record_status_id: data.record_status_id });

      if (!updatedDrug) return;

      await queryClient.invalidateQueries(DrugsQueryKeys.refreshCurrentSearch());
      setSelectedRow(updatedDrug);

      if (preferredTab !== CURRENT_TAB) {
        setActiveTab(preferredTab);
        return;
      }

      await nomenclatureCreateEvent.emit(drug);
      onClose();
    } catch (error) {
      setGlobalError(error);
    }
  };

  const handleNomenclatureConfirmationEditDialogClose = () =>
    preferredTab === CURRENT_TAB ? onClose() : setActiveTab(preferredTab);

  useUpdateEffect(() => {
    if (preferredTab === CURRENT_TAB) return;

    latestFormState.current.isDirty ? openConfirmationEditDialog() : setActiveTab(preferredTab);
  }, [preferredTab]);

  return (
    <FormProvider methods={methods} onSubmit={submitHandler}>
      <ModalBody
        sidebar={
          <NomenclatureModalSidebar drug={drug} isEditMode={isEditMode || isEditAfterCreationMode} onCancel={onClose} />
        }
      >
        <ModalBodyContent>
          <Section title="Скрипты">
            <Stack spacing={2} direction="row">
              <LabeledTextarea name="replacement_script" label="На замену" />
              <LabeledTextarea name="sale_script" label="На допродажу" />
            </Stack>
          </Section>

          <Section title="Рекомендации">
            <TextEditorContainer variant="outlined">
              <RHFTextEditor name="recommendations" placeholder="Введите текст инструкции" />
            </TextEditorContainer>
          </Section>
        </ModalBodyContent>
      </ModalBody>

      <NomenclatureConfirmationEditDialog
        isLoading={isSubmitting}
        onProceed={handleSubmit(submitHandler)}
        onClose={handleNomenclatureConfirmationEditDialogClose}
      />
    </FormProvider>
  );
};
